import type {
  AutomationRequestStatus,
  SuggestionColor,
  SuggestionIcon,
  SuggestionResponseType,
  SuggestionStatus,
} from '@adweb/shared';
import {
  BarChart3,
  BookOpen,
  Bot,
  Calendar,
  Database,
  FileText,
  Mail,
  MessageSquare,
  Phone,
  Receipt,
  Search,
  ShoppingCart,
  Sparkles,
  Users,
  type LucideIcon,
} from 'lucide-react';

/** Champs communs aux vues client et Adweb. */
export interface SuggestionBase {
  id: string;
  title: string;
  tagline: string;
  description: string;
  example: string | null;
  category: string | null;
  hoursSavedPerMonth: number | null;
  icon: SuggestionIcon;
  color: SuggestionColor;
  publishedAt: string | null;
}

/** Vue client : réponse de son organisation et badge « Nouveau ». */
export interface ClientSuggestion extends SuggestionBase {
  isNew: boolean;
  response: {
    type: SuggestionResponseType;
    createdAt: string;
    byName: string | null;
    /** Présent seulement si l'utilisateur peut ouvrir la demande. */
    requestId: string | null;
  } | null;
}

/** Vue Adweb : statut, ciblage et intérêt des clients. */
export interface AdminSuggestion extends SuggestionBase {
  status: SuggestionStatus;
  targetAll: boolean;
  targets: Array<{ id: string; name: string }>;
  interestedCount: number;
  dismissedCount: number;
  createdBy: { id: string; fullName: string } | null;
  updatedAt: string;
  /** Détail uniquement. */
  responses?: Array<{
    type: SuggestionResponseType;
    createdAt: string;
    requestId: string | null;
    organization: { id: string; name: string };
    user: { id: string; fullName: string } | null;
    request: { status: AutomationRequestStatus } | null;
  }>;
}

export const SUGGESTION_ICON: Record<SuggestionIcon, { icon: LucideIcon; label: string }> = {
  sparkles: { icon: Sparkles, label: 'IA' },
  bot: { icon: Bot, label: 'Assistant' },
  message: { icon: MessageSquare, label: 'Messages' },
  mail: { icon: Mail, label: 'E-mail' },
  file: { icon: FileText, label: 'Documents' },
  calendar: { icon: Calendar, label: 'Agenda' },
  users: { icon: Users, label: 'Clients / équipe' },
  chart: { icon: BarChart3, label: 'Reporting' },
  search: { icon: Search, label: 'Recherche' },
  database: { icon: Database, label: 'Données' },
  receipt: { icon: Receipt, label: 'Facturation' },
  cart: { icon: ShoppingCart, label: 'Commandes' },
  phone: { icon: Phone, label: 'Téléphone' },
  book: { icon: BookOpen, label: 'Connaissances' },
};

/** Pastille d'icône et liseré de tuile (familles de couleurs suivies par le mode sombre). */
export const SUGGESTION_COLOR: Record<
  SuggestionColor,
  { chip: string; bar: string; label: string }
> = {
  blue: { chip: 'bg-blue-100 text-blue-700', bar: 'bg-blue-500', label: 'Bleu' },
  violet: { chip: 'bg-violet-100 text-violet-700', bar: 'bg-violet-500', label: 'Violet' },
  emerald: { chip: 'bg-emerald-100 text-emerald-700', bar: 'bg-emerald-500', label: 'Vert' },
  amber: { chip: 'bg-amber-100 text-amber-700', bar: 'bg-amber-500', label: 'Jaune' },
  orange: { chip: 'bg-orange-100 text-orange-700', bar: 'bg-orange-500', label: 'Orange' },
  slate: { chip: 'bg-slate-100 text-slate-700', bar: 'bg-slate-500', label: 'Gris' },
};

export const SUGGESTION_STATUS_META: Record<SuggestionStatus, { label: string; cls: string }> = {
  DRAFT: { label: 'Brouillon', cls: 'bg-amber-100 text-amber-800' },
  PUBLISHED: { label: 'Publiée', cls: 'bg-emerald-100 text-emerald-800' },
  ARCHIVED: { label: 'Archivée', cls: 'bg-slate-100 text-slate-600' },
};

/** « ≈ 6 h gagnées / mois » (null si non renseigné). */
export function gainLabel(hours: number | null): string | null {
  if (!hours) return null;
  const value = Number.isInteger(hours) ? String(hours) : hours.toFixed(1).replace('.', ',');
  return `≈ ${value} h gagnées / mois`;
}

export const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });

export function apiErrorMessage(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { message?: string } } } | null)?.response?.data;
  return data?.message ?? fallback;
}
